/**
 * Guía plegable para sacar de TRIBU-CR la llave criptográfica o las
 * credenciales de comprobantes. Plegable y no tooltip: en el celular un
 * tooltip no se abre.
 */

import { useState } from 'react'
import { AlertTriangle, ChevronDown, ExternalLink, HelpCircle, Info } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

export const TRIBU_CR_URL = 'https://ovitribucr.hacienda.go.cr'

interface TribuCrGuideProps {
  topic: 'key' | 'credentials'
  isSandbox: boolean
  defaultOpen?: boolean
}

export function TribuCrGuide({ topic, isSandbox, defaultOpen = false }: TribuCrGuideProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(defaultOpen)

  const steps = [
    t('settings.tribuGuide.stepLogin'),
    t('settings.tribuGuide.stepMenu'),
    t('settings.tribuGuide.stepOtherProgram'),
    topic === 'key'
      ? t(isSandbox ? 'settings.tribuGuide.keyStepSandbox' : 'settings.tribuGuide.keyStepProduction')
      : t(
          isSandbox
            ? 'settings.tribuGuide.credentialsStepSandbox'
            : 'settings.tribuGuide.credentialsStepProduction'
        ),
  ]

  const notes: { text: string; warning?: boolean }[] = []
  if (isSandbox) notes.push({ text: t('settings.tribuGuide.sandboxNote') })
  if (topic === 'key' && !isSandbox) {
    notes.push({ text: t('settings.tribuGuide.revokeWarning'), warning: true })
  }
  if (topic === 'credentials') notes.push({ text: t('settings.tribuGuide.notOviPassword') })

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border bg-muted/30">
      <CollapsibleTrigger className="flex w-full min-h-12 items-center gap-2 px-4 py-3 text-left">
        <HelpCircle className="h-4 w-4 shrink-0 text-primary" />
        <span className="flex-1 text-sm font-medium">
          {t(topic === 'key' ? 'settings.tribuGuide.titleKey' : 'settings.tribuGuide.titleCredentials')}
        </span>
        <Badge variant={isSandbox ? 'secondary' : 'default'} className="shrink-0">
          {t(isSandbox ? 'settings.tribuGuide.sandboxBadge' : 'settings.tribuGuide.productionBadge')}
        </Badge>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-4 border-t px-4 py-4">
        <ol className="space-y-3">
          {steps.map((step, index) => (
            <li key={index} className="flex gap-3 text-sm">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                {index + 1}
              </span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>

        {notes.map((note) => (
          <div
            key={note.text}
            className={cn(
              'flex gap-2 rounded-md p-3 text-sm',
              note.warning
                ? 'bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-200'
                : 'bg-background text-muted-foreground'
            )}
          >
            {note.warning ? (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span>{note.text}</span>
          </div>
        ))}

        <a
          href={TRIBU_CR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-md border bg-background text-sm font-medium hover:bg-accent sm:w-auto sm:px-6"
        >
          {t('settings.tribuGuide.openPortal')}
          <ExternalLink className="h-4 w-4" />
        </a>

        <p className="text-xs text-muted-foreground">{t('settings.tribuGuide.menusMayVary')}</p>
      </CollapsibleContent>
    </Collapsible>
  )
}
